import { MouseEvent, useEffect, useRef, useState } from "react";
import _ from "lodash";
import Delta from "quill-delta";
import createDOMPurify from "dompurify";
import { useNavigate } from "react-router-dom";

import { FormatType, TextElementProps, TextType } from "./types";
import Quill, { READ_ONLY_TABLE_MODULE } from "@/libraries/quill";
import { rewriteMarkersForDisplay } from "@/shared/media";

import "./styles.css";

const SANITIZE_CONFIG = {
  USE_PROFILES: { html: true },
  ADD_ATTR: [
    "contenteditable",
    "data-id",
    "data-value",
    "data-denotation-char",
    "data-index",
    "data-link",
  ],
};

const TextElement = ({
  value,
  format,
  type = TextType.REGULAR,
  multiple = false,
  lineClampNumber,
}: TextElementProps) => {
  const quillRefs = useRef<{ [key: string]: Quill | null }>({});
  const DOMPurify = createDOMPurify(window);
  const navigate = useNavigate();
  const [deltaIds, setDeltaIds] = useState<Record<number, string>>({});

  const clampStyles = lineClampNumber
    ? {
        display: "-webkit-box",
        WebkitLineClamp: lineClampNumber,
        WebkitBoxOrient: "vertical" as const,
        overflow: "hidden",
      }
    : undefined;

  const handleMentionClick = (e: MouseEvent<HTMLElement>) => {
    const mention = (e.target as HTMLElement).closest(".mention");
    if (!mention) return;
    const userId = (mention as HTMLElement).dataset.id;
    const denotation = (mention as HTMLElement).dataset.denotationChar;
    if (denotation === "@" && userId) {
      e.preventDefault();
      navigate(`/users/${userId}`);
    }
  };

  useEffect(() => {
    if (type !== TextType.RICH_TEXT) return;

    const values = Array.isArray(value) ? value : [value];
    const ids: Record<number, string> = {};
    values.forEach((val, index) => {
      if (val != null && val !== "") {
        ids[index] = `quill-read-${_.uniqueId()}`;
      }
    });
    setDeltaIds(ids);

    return () => {
      quillRefs.current = {};
    };
  }, [value, multiple, type]);

  useEffect(() => {
    if (type !== TextType.RICH_TEXT) return;
    const values = Array.isArray(value) ? value : [value];

    Object.entries(deltaIds).forEach(([indexStr, id]) => {
      const val = values[Number(indexStr)];
      if (val == null || val === "") return;

      const tryMountOrUpdate = (attempt: number) => {
        const existing = quillRefs.current[id];
        if (existing) {
          if (val instanceof Delta) {
            existing.setContents(val);
          } else {
            const sanitized = DOMPurify.sanitize(
              rewriteMarkersForDisplay(val),
              SANITIZE_CONFIG,
            );
            const delta = existing.clipboard.convert({ html: sanitized });
            existing.setContents(delta);
          }
          return;
        }
        const element = document.getElementById(id);
        if (!element) {
          if (attempt < 10) {
            requestAnimationFrame(() => tryMountOrUpdate(attempt + 1));
          }
          return;
        }
        const quill = new Quill(`#${id}`, {
          readOnly: true,
          modules: {
            toolbar: [],
            table: false,
            "table-better": READ_ONLY_TABLE_MODULE,
          },
        });
        if (val instanceof Delta) {
          quill.setContents(val);
        } else {
          const sanitized = DOMPurify.sanitize(
            rewriteMarkersForDisplay(val),
            SANITIZE_CONFIG,
          );
          const delta = quill.clipboard.convert({ html: sanitized });
          quill.setContents(delta);
        }
        quillRefs.current[id] = quill;
      };
      setTimeout(() => tryMountOrUpdate(0), 0);
    });
  }, [deltaIds, value, multiple, type]);

  const renderRichTextItem = (
    val: string | Delta | undefined | null,
    index: number,
  ) => {
    if (val == null || val === "") return null;
    const id = deltaIds[index];
    return id ? (
      <div id={id} className="quill-read-only" style={clampStyles}></div>
    ) : null;
  };

  const renderValue = (val: string | undefined | null | Delta) => {
    if (!val || val instanceof Delta) return null;

    if (
      format &&
      [FormatType.LINK, FormatType.IMAGE, FormatType.FILE].includes(format)
    ) {
      return (
        <a
          href={val}
          target="_blank"
          rel="noopener noreferrer"
          style={clampStyles}
        >
          {val}
        </a>
      );
    }
    return <span style={clampStyles}>{val}</span>;
  };

  if (type === TextType.RICH_TEXT) {
    const valueArray = Array.isArray(value) ? value : value ? [value] : [];

    if (multiple) {
      return (
        <ul className="list-disc pl-5" onClick={handleMentionClick}>
          {valueArray.map((item, index) => (
            <li key={index} className="mb-1">
              {renderRichTextItem(item, index)}
            </li>
          ))}
        </ul>
      );
    }

    const single = valueArray[0];
    if (single == null) return null;
    return (
      <div onClick={handleMentionClick}>{renderRichTextItem(single, 0)}</div>
    );
  }

  if (multiple) {
    const valueArray = Array.isArray(value) ? value : value ? [value] : [];

    if (valueArray.length > 0) {
      return (
        <ul className="list-disc pl-5">
          {valueArray.map((item, index) => (
            <li key={index} className="mb-1">
              {renderValue(item)}
            </li>
          ))}
        </ul>
      );
    }

    return <ul className="list-disc pl-5"></ul>;
  }

  const singleValue = Array.isArray(value) ? value[0] : value;
  return renderValue(singleValue);
};

export default TextElement;
